import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { Bell } from 'lucide-react';
import { clsx } from '../ds/clsx';
import {
  Avatar,
  Caret,
  IconButton,
  KeyboardHint,
  MenuHeader,
  MenuItem,
  MenuList,
  Popover,
} from '../ds/components';
import { useOutsideClick } from '../ds/hooks';
import { isMacPlatform } from '../lib/platform';
import { WorkspaceSwitcher, type WorkspaceOption } from './WorkspaceSwitcher';
import {
  navigationFor,
  type ActiveNavigation,
  type NavAccess,
  type NavGroupKey,
  type ResolvedNavGroup,
} from './navigation';
import styles from './Topbar.module.css';

export interface TopbarUser {
  name: string;
  initials: string;
  roleLabel: string;
  avatarUrl: string | null;
}

export interface TopbarProps {
  /** The current top-level entry and dropdown entry (`activeNavigation`). */
  active: ActiveNavigation;
  /** The settings pages are open: the profile trigger is highlighted. */
  settingsActive: boolean;
  user: TopbarUser;
  /** Tool access, permissions and modules from the session (they decide which entries appear). */
  access: NavAccess;
  /** Open tasks assigned to the user (D-38): the badge on „Tasks“ is hidden at 0. */
  taskCount: number;
  unread: number;
  queuePaused: boolean;
  /** Set when the account belongs to more than one workspace: the switcher appears next to the logo. */
  workspace?: { name: string; options: WorkspaceOption[] } | undefined;
  /** ≤ 1360 px: search 200 px, name hidden. */
  compact?: boolean | undefined;
  /** Rounded top corners (the topbar inside a rounded frame). */
  rounded?: boolean | undefined;
  /** Forces a menu open (documentation and screenshots). */
  menuOpen?: NavGroupKey | 'profile' | undefined;
  notificationsOpen: boolean;
  /** Controlled state of the workspace switcher popover (the mobile "Mehr" page opens it too). */
  switcherOpen: boolean;
  onSwitcherOpenChange: (open: boolean) => void;
  onOpenSearch: () => void;
  onToggleNotifications: () => void;
  onResumeQueue: () => void;
  onLogout: () => void;
  onSwitchWorkspace: (organizationId: string) => Promise<void> | void;
  /** Ref of the bell, so the notifications panel can return focus to it. */
  bellRef?: React.RefObject<HTMLButtonElement | null> | undefined;
  children?: ReactNode;
}

/** The shared navigation bar (docs/extracts/shell.md A2 "Topbar-Komponente"): 60 px, for every role. */
export function Topbar({
  active,
  settingsActive,
  user,
  access,
  taskCount,
  unread,
  queuePaused,
  workspace,
  compact = false,
  rounded = false,
  menuOpen,
  notificationsOpen,
  switcherOpen,
  onSwitcherOpenChange,
  onOpenSearch,
  onToggleNotifications,
  onResumeQueue,
  onLogout,
  onSwitchWorkspace,
  bellRef,
}: TopbarProps) {
  const navigate = useNavigate();
  const [hoverGroup, setHoverGroup] = useState<NavGroupKey | null>(null);
  const [pinnedGroup, setPinnedGroup] = useState<NavGroupKey | null>(null);
  const [profile, setProfile] = useState(false);
  const switcher = Boolean(workspace) && switcherOpen;
  const setSwitcher = onSwitcherOpenChange;
  const navRef = useRef<HTMLElement>(null);
  const profileWrapRef = useRef<HTMLDivElement>(null);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const workspaceButtonRef = useRef<HTMLButtonElement>(null);

  const entries = navigationFor(access);
  const profileOpen = menuOpen === 'profile' || profile;
  const shortcut = isMacPlatform() ? '⌘K' : 'Strg K';

  useOutsideClick([navRef], () => setPinnedGroup(null), pinnedGroup !== null);
  useOutsideClick([profileWrapRef], () => setProfile(false), profile);

  useEffect(() => {
    // Any navigation closes the open menus.
    setPinnedGroup(null);
    setHoverGroup(null);
    setProfile(false);
  }, [active.top, active.item, settingsActive]);

  function groupOpen(key: NavGroupKey): boolean {
    return menuOpen === key || hoverGroup === key || pinnedGroup === key;
  }

  function closeGroups(returnFocus?: NavGroupKey) {
    setPinnedGroup(null);
    setHoverGroup(null);
    if (returnFocus) {
      navRef.current?.querySelector<HTMLButtonElement>(`[data-group="${returnFocus}"]`)?.focus();
    }
  }

  function closeProfile(returnFocus = false) {
    setProfile(false);
    if (returnFocus) profileButtonRef.current?.focus();
  }

  function onGroupKeyDown(key: NavGroupKey, event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setPinnedGroup(key);
    } else if (event.key === 'Escape' && groupOpen(key)) {
      event.preventDefault();
      closeGroups(key);
    }
  }

  function onProfileKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setProfile(true);
    }
  }

  function go(href: string) {
    closeGroups();
    closeProfile();
    void navigate(href);
  }

  function renderGroup(group: ResolvedNavGroup) {
    const open = groupOpen(group.key);
    const groupActive = active.top === group.key;
    const activeItem = groupActive ? group.items.find((item) => item.key === active.item) : undefined;
    return (
      <div
        key={group.key}
        className={styles.toolsWrap}
        onMouseEnter={() => setHoverGroup(group.key)}
        onMouseLeave={() => setHoverGroup((current) => (current === group.key ? null : current))}
      >
        <button
          type="button"
          data-group={group.key}
          aria-haspopup="menu"
          aria-expanded={open}
          className={clsx(styles.toolsButton, groupActive && styles.toolsActive, open && styles.toolsOpen)}
          onClick={() => setPinnedGroup((pinned) => (pinned === group.key ? null : group.key))}
          onKeyDown={(event) => onGroupKeyDown(group.key, event)}
        >
          {group.label}
          {activeItem && !compact ? <span className={styles.toolName}>· {activeItem.label}</span> : null}
          <Caret size={9} />
        </button>
        {open ? (
          <div className={styles.toolsMenu}>
            <MenuList
              label={group.label}
              autoFocus={pinnedGroup === group.key}
              onClose={() => closeGroups(group.key)}
            >
              {group.items.map((item) => (
                <MenuItem
                  key={item.key}
                  label={item.label}
                  description={item.locked ? 'Nicht freigeschaltet' : item.description}
                  badge={item.locked ? 'kein Zugriff' : undefined}
                  active={groupActive && active.item === item.key}
                  className={clsx(item.locked && styles.toolLocked)}
                  onSelect={() => go(item.href)}
                />
              ))}
            </MenuList>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <header className={clsx(styles.topbar, rounded && styles.rounded, compact && styles.compact)}>
      <Link to="/home" className={styles.logo} aria-label="DAMOKLES — zur Startseite">
        DAMOKLES
      </Link>

      {workspace ? (
        <>
          <button
            ref={workspaceButtonRef}
            type="button"
            className={styles.workspace}
            aria-haspopup="dialog"
            aria-expanded={switcher}
            onClick={() => setSwitcher(!switcher)}
          >
            <span className={styles.workspaceName}>{workspace.name}</span>
            <Caret size={9} />
          </button>
          <Popover
            open={switcher}
            onClose={() => setSwitcher(false)}
            anchorRef={workspaceButtonRef}
            placement="bottom-start"
            offset={8}
            width={300}
            label="Workspace wechseln"
            initialFocus="first"
          >
            <WorkspaceSwitcher
              options={workspace.options}
              onSelect={async (organizationId) => {
                await onSwitchWorkspace(organizationId);
                setSwitcher(false);
              }}
            />
          </Popover>
        </>
      ) : null}

      <nav ref={navRef} className={styles.nav} aria-label="Hauptnavigation">
        {entries.map((entry) => {
          if (entry.kind === 'group') return renderGroup(entry);
          const isActive = active.top === entry.key;
          return (
            <Link
              key={entry.key}
              to={entry.href}
              className={clsx(styles.navItem, isActive && styles.navActive, entry.locked && styles.navLocked)}
              aria-current={isActive ? 'page' : undefined}
              title={entry.locked ? 'Nicht freigeschaltet · kein Zugriff' : undefined}
            >
              {entry.label}
              {entry.key === 'tasks' && taskCount > 0 ? (
                <span className={styles.taskBadge} aria-label={`${taskCount} offene Tasks`}>
                  {taskCount}
                </span>
              ) : null}
            </Link>
          );
        })}
      </nav>

      <div className={styles.right}>
        {queuePaused ? (
          <button type="button" className={styles.queuePill} onClick={onResumeQueue}>
            <span className={styles.queueDot} aria-hidden="true" />
            Queue pausiert · fortsetzen
          </button>
        ) : null}

        <button
          type="button"
          className={styles.search}
          onClick={onOpenSearch}
          aria-keyshortcuts="Meta+K Control+K"
        >
          <span className={styles.searchLabel}>Suchen</span>
          <KeyboardHint keys={shortcut} />
        </button>

        <IconButton
          ref={bellRef}
          icon={Bell}
          label="Benachrichtigungen"
          badge={unread}
          active={notificationsOpen}
          aria-haspopup="dialog"
          aria-expanded={notificationsOpen}
          onClick={onToggleNotifications}
        />

        <div ref={profileWrapRef} className={styles.profileWrap}>
          <button
            ref={profileButtonRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={profileOpen}
            aria-label={compact ? user.name : undefined}
            className={clsx(styles.profile, (settingsActive || profileOpen) && styles.profileActive)}
            onClick={() => setProfile((open) => !open)}
            onKeyDown={onProfileKeyDown}
          >
            <Avatar initials={user.initials} name={user.name} size={26} src={user.avatarUrl ?? undefined} />
            {!compact ? <span className={styles.profileName}>{user.name}</span> : null}
            <Caret size={9} />
          </button>
          {profileOpen ? (
            <div className={styles.profileMenu}>
              <MenuList label="Profil" onClose={() => closeProfile(true)}>
                <MenuHeader title={user.name} subtitle={user.roleLabel} />
                <MenuItem label="Mein Profil" onSelect={() => go('/settings/profile')} />
                <MenuItem label="Einstellungen" active={settingsActive} onSelect={() => go('/settings')} />
                {workspace ? (
                  <MenuItem
                    label="Workspace wechseln"
                    onSelect={() => {
                      closeProfile();
                      setSwitcher(true);
                    }}
                  />
                ) : null}
                <MenuItem
                  label="Abmelden"
                  tone="muted"
                  onSelect={() => {
                    closeProfile();
                    onLogout();
                  }}
                />
              </MenuList>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}
